import { useProductEditor } from '../product-editor-context'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Plus, Trash2, FormInput } from 'lucide-react'
import type { CustomInputDefinition } from '@/types/database'

function generateId() {
  return crypto.randomUUID().slice(0, 8)
}

const INPUT_TYPE_LABELS: Record<CustomInputDefinition['type'], string> = {
  text: 'Short Text',
  textarea: 'Long Text',
  image_upload: 'Image Upload',
  file_upload: 'File Upload',
}

export function CustomInputsSection() {
  const { customInputs, setCustomInputs } = useProductEditor()

  const addInput = () => {
    const newInput: CustomInputDefinition = {
      id: generateId(),
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      position: customInputs.length,
    }
    setCustomInputs([...customInputs, newInput])
  }

  const removeInput = (index: number) => {
    setCustomInputs(
      customInputs.filter((_, i) => i !== index).map((input, i) => ({ ...input, position: i }))
    )
  }

  const updateInput = (index: number, updates: Partial<CustomInputDefinition>) => {
    const newInputs = [...customInputs]
    newInputs[index] = { ...newInputs[index], ...updates }
    setCustomInputs(newInputs)
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FormInput className="size-5 text-zinc-500" />
            <div>
              <CardTitle className="text-base">Customer Inputs</CardTitle>
              <CardDescription>
                Fields customers fill out when ordering (personalization, files, etc.)
              </CardDescription>
            </div>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addInput}>
            <Plus className="mr-1 size-3" /> Add field
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {customInputs.map((input, i) => (
          <div key={input.id} className="space-y-3 rounded-lg border border-zinc-200 bg-zinc-50 p-4">
            <div className="flex items-start gap-3">
              <div className="grid flex-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs text-zinc-500">Label</Label>
                  <Input
                    value={input.label}
                    onChange={e => updateInput(i, { label: e.target.value })}
                    placeholder="e.g., Engraving text"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Type</Label>
                  <Select
                    value={input.type}
                    onValueChange={(value: CustomInputDefinition['type']) => updateInput(i, { type: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.entries(INPUT_TYPE_LABELS) as [CustomInputDefinition['type'], string][]).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeInput(i)}
                className="mt-5 text-red-500 hover:text-red-600 hover:bg-red-50"
              >
                <Trash2 className="size-4" />
              </Button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs text-zinc-500">Placeholder</Label>
                <Input
                  value={input.placeholder || ''}
                  onChange={e => updateInput(i, { placeholder: e.target.value })}
                  placeholder="Placeholder text"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Help text</Label>
                <Input
                  value={input.help_text || ''}
                  onChange={e => updateInput(i, { help_text: e.target.value })}
                  placeholder="Instructions for the customer"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={input.required}
                  onCheckedChange={(checked: boolean) => updateInput(i, { required: checked })}
                  id={`required-${input.id}`}
                />
                <Label htmlFor={`required-${input.id}`} className="text-sm text-zinc-600">
                  Required
                </Label>
              </div>
              {(input.type === 'text' || input.type === 'textarea') && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-zinc-500">Max length</Label>
                  <Input
                    type="number"
                    min="1"
                    value={input.max_length || ''}
                    onChange={e => updateInput(i, { max_length: parseInt(e.target.value) || undefined })}
                    className="h-8 w-20"
                  />
                </div>
              )}
            </div>
          </div>
        ))}

        {customInputs.length === 0 && (
          <p className="text-sm text-zinc-400 text-center py-4">
            No customer input fields. Add fields if customers need to provide custom text, images, etc.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
